/**
 * Where a card has been.
 *
 * How it arrived, then every time it changed hands, oldest first, with whatever
 * anyone wrote down or photographed along the way. Cards keep their id through
 * every trade and every deal, so this is read straight from those records;
 * nothing is written here.
 *
 * Three kinds of moment:
 *   origin  it appeared in somebody's collection, through a door it now names
 *   trade   two people swapped, with everything that went the other way
 *   deal    a vendor counter, with everything else that was on the table
 *
 * A swap is ONE moment, not one per card. Three cards for three is a single
 * step listing all six, because that is how the person remembers it and
 * splitting it into six lines loses the fact that they were one decision.
 */

import { useEffect, useState } from 'react';
import { ScanLine, ArrowLeftRight, Quote, Store, Sparkles, MapPin } from 'lucide-react';
import { journey, money } from '../../services/tradeNow';

const when = (ts) => {
  if (!ts) return '';
  const d = new Date(ts);
  return `${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}.${d.getFullYear()}`;
};

/** What the door was called, in the words somebody would use out loud. */
const ORIGIN_LABEL = {
  scan: 'Scanned in',
  vendor: 'Came in at a counter',
  trade: 'Came in on a trade',
  manual: 'Added by hand',
};

/**
 * Which way a card went in a vendor deal, said plainly. A vendor picks only in
 * or out, so the journey does not claim to know whether it was a sale or a trade.
 */
const DEAL_LABEL = {
  buy: 'Came in',
  trade_in: 'Came in',
  sell: 'Went out',
  trade_out: 'Went out',
};

const isArriving = (kind) => kind === 'buy' || kind === 'trade_in';

/** The photos on a memory, from either the list or the single column it replaced. */
function photosOf(m) {
  if (Array.isArray(m?.photo_urls) && m.photo_urls.length) return m.photo_urls.filter(Boolean);
  return m?.photo_url ? [m.photo_url] : [];
}

/**
 * @param {boolean} hideWhenEmpty  draw nothing at all when there is no story.
 *   True where this sits among other content and a heading over one line would
 *   be noise. False on the journey's own screen, where somebody has deliberately
 *   come to look and deserves an answer rather than a blank.
 */
export default function CardJourney({ cardId, theme, titleStyle, hideWhenEmpty = true }) {
  const [rows, setRows] = useState(null);

  useEffect(() => {
    if (!cardId) return undefined;
    let alive = true;
    journey(cardId).then((r) => { if (alive) setRows(r); }).catch(() => { if (alive) setRows([]); });
    return () => { alive = false; };
  }, [cardId]);

  // A card that has only ever sat in one collection has no journey to tell, and
  // a heading over a single line saying "scanned" is worse than saying nothing.
  const hasStory = (rows || []).some((r) => r.kind === 'trade' || r.kind === 'deal');
  const s = makeStyles(theme);

  if (!rows) return null;
  if (!hasStory) {
    if (hideWhenEmpty) return null;
    return (
      <div style={s.wrap}>
        <div style={s.blank}>
          <MapPin size={22} color={theme.textMuted} />
          <div style={s.blankTitle}>No journey yet</div>
          <div style={s.blankBody}>
            Trade this card or move it across a table, and every step lands here
            with whatever you write down at the time.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={s.wrap}>
      <div style={titleStyle}>Trade Journey</div>
      <div style={s.line}>
        {rows.map((r, i) => (
          <div key={`${r.kind}-${i}`} style={s.step}>
            <div style={s.icon}>
              {r.kind === 'origin' ? <ScanLine size={14} />
                : r.kind === 'deal' ? <Store size={14} />
                  : <ArrowLeftRight size={14} />}
            </div>

            <div style={s.body}>
              <div style={s.head}>
                <span style={s.what}>{headline(r)}</span>
                <span style={s.date}>{when(r.at)}</span>
              </div>

              {r.kind === 'origin' && (
                <div style={s.meta}>
                  {[
                    r.condition ? `${r.condition} when it came in` : null,
                    r.cost_basis != null ? `${money(r.cost_basis)} paid` : null,
                  ].filter(Boolean).join(' · ')}
                </div>
              )}

              {r.kind === 'trade' && (
                <div style={s.meta}>
                  {[
                    r.agreed_condition ? `${r.agreed_condition} agreed` : null,
                    r.price_at_trade != null ? `${money(r.price_at_trade)} at the time` : null,
                  ].filter(Boolean).join(' · ')}
                </div>
              )}

              {r.kind === 'deal' && (
                <div style={s.meta}>
                  {[
                    r.price != null ? `${money(r.price)} on the card` : null,
                    r.cost_basis != null ? `${money(r.cost_basis)} changed hands` : null,
                  ].filter(Boolean).join(' · ')}
                </div>
              )}

              {/* The other side of the swap. Named rather than counted: "for
                  your Pikachu and Machop" is the story; "2 cards" is a receipt. */}
              {r.kind === 'trade' && !!r.traded_for?.length && (
                <div style={s.swap}>
                  <span style={s.swapLabel}>In exchange for</span>
                  <span style={s.swapNames}>{r.traded_for.map((t) => t.name).join(', ')}</span>
                </div>
              )}

              {r.kind === 'deal' && !!r.alongside?.length && (
                <div style={s.swap}>
                  <span style={s.swapLabel}>
                    {isArriving(r.deal_kind) ? 'Against' : 'Alongside'}
                  </span>
                  <span style={s.swapNames}>{r.alongside.map((t) => t.name).join(', ')}</span>
                </div>
              )}

              {/* A vendor deal's own note and photos. */}
              {r.kind === 'deal' && (r.note || (r.photo_urls || []).length > 0) && (
                <div style={s.memory}>
                  <Sparkles size={12} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {r.note && <div style={s.note}>{r.note}</div>}
                    <Shots urls={r.photo_urls} s={s} />
                  </div>
                </div>
              )}

              {/* Both traders can keep their own, so there may be two. */}
              {(r.memories || []).map((m, j) => {
                const shots = photosOf(m);
                if (!m.note && shots.length === 0) return null;
                return (
                  <div key={j} style={s.memory}>
                    <Quote size={12} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      {m.note && <div style={s.note}>{m.note}</div>}
                      <Shots urls={shots} s={s} />
                      <div style={s.by}>{m.username || 'A trader'}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/** What happened, in one line. */
function headline(r) {
  // A traded card sends no `via`, because the column describing the door now
  // describes the LATEST owner's door rather than the first one's. So the first
  // step says only that this is where the card appeared.
  if (r.kind === 'origin') return ORIGIN_LABEL[r.via] || 'First in a collection';
  if (r.kind === 'deal') return DEAL_LABEL[r.deal_kind] || 'At a vendor counter';
  return `${r.from?.username || 'Someone'} traded it to ${r.to?.username || 'someone'}`;
}

/**
 * Photos in a row that scrolls rather than a column that pushes the step apart.
 *
 * The alt text is real rather than empty. An empty alt takes an image out of the
 * accessibility tree entirely, which is right for decoration and wrong here:
 * these are the photographs somebody deliberately kept of a trade, and they are
 * content.
 */
function Shots({ urls, s, label = 'Photo kept with this moment' }) {
  const list = (urls || []).filter(Boolean);
  if (list.length === 0) return null;
  if (list.length === 1) return <img src={list[0]} alt={label} style={s.photo} />;
  return (
    <div style={s.shots}>
      {list.map((u, i) => (
        <img key={u} src={u} alt={`${label} (${i + 1} of ${list.length})`} style={s.shot} />
      ))}
    </div>
  );
}

const makeStyles = (theme) => ({
  wrap: { marginTop: 22, marginBottom: 12 },
  line: { display: 'flex', flexDirection: 'column', gap: 0, borderLeft: `2px solid ${theme.border}`, marginLeft: 13, paddingLeft: 0 },
  step: { display: 'flex', gap: 12, padding: '6px 0 14px', position: 'relative' },
  icon: { width: 28, height: 28, borderRadius: 14, background: theme.bgCard, border: `2px solid ${theme.border}`, color: theme.accent, display: 'grid', placeItems: 'center', marginLeft: -15, flexShrink: 0 },
  body: { flex: 1, minWidth: 0 },
  head: { display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'baseline' },
  what: { fontSize: 14, fontWeight: 600, color: theme.text },
  date: { fontSize: 12, color: theme.textMuted, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' },
  meta: { fontSize: 12, color: theme.textSecondary, marginTop: 2, lineHeight: 1.4 },
  swap: { display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'baseline', marginTop: 6 },
  swapLabel: { fontSize: 11, fontWeight: '700', letterSpacing: '0.04em', textTransform: 'uppercase', color: theme.textMuted },
  swapNames: { fontSize: 13, color: theme.text, lineHeight: 1.4, minWidth: 0 },
  memory: { display: 'flex', gap: 8, marginTop: 8, padding: 10, borderRadius: 10, background: theme.bgCard, border: `1px solid ${theme.border}`, color: theme.textMuted },
  note: { fontSize: 13, color: theme.text, lineHeight: 1.45 },
  photo: { width: '100%', maxHeight: 180, objectFit: 'cover', borderRadius: 8, marginTop: 8, display: 'block' },
  shots: { display: 'flex', gap: 6, marginTop: 8, overflowX: 'auto', paddingBottom: 2 },
  shot: { width: 108, height: 108, objectFit: 'cover', borderRadius: 8, flex: '0 0 auto' },
  by: { fontSize: 11, color: theme.textMuted, marginTop: 4 },
  blank: {
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
    textAlign: 'center', padding: '40px 24px',
  },
  blankTitle: { fontSize: 16, fontWeight: '700', color: theme.text },
  blankBody: { fontSize: 13, color: theme.textSecondary, lineHeight: 1.5, maxWidth: 300 },
});
